import { useContext, useMemo } from 'react'

import { Maybe, SanityContextBasedContent } from '../../../graphql/gatsby'
import { SanitySiteContext } from '../context'
import { SanityContext } from '../context-based-content'

export type UseSanityContextBasedContentOptions = SanityShouldDisplayContextBasedContentOptions

export type UseSanityContextBasedContentLazy = UseSanityContextBasedContent & {
  shouldDisplayContent: (options: SanityShouldDisplayContextBasedContentOptions) => boolean
}

export type UseSanityContextBasedContentEager = UseSanityContextBasedContent & {
  shouldDisplay: boolean
}

interface UseSanityContextBasedContent {
  currentContext: SanityContext | null
}

export type SanityShouldDisplayContextBasedContent = (
  options: SanityShouldDisplayContextBasedContentOptions,
) => boolean

export interface SanityShouldDisplayContextBasedContentOptions {
  contextBasedContent?: Maybe<SanityContextBasedContent>
}

const shouldDisplayContentFor =
  (sanityContext: SanityContext) =>
  ({ contextBasedContent }: SanityShouldDisplayContextBasedContentOptions): boolean => {
    if (!contextBasedContent || !contextBasedContent.enabled) {
      return true
    }

    return contextBasedContent?.contexts?.some((context) => context === sanityContext) ?? false
  }

export function useSanityContextBasedContent(): UseSanityContextBasedContentLazy
export function useSanityContextBasedContent(
  options: UseSanityContextBasedContentOptions,
): UseSanityContextBasedContentEager
export function useSanityContextBasedContent(
  options?: UseSanityContextBasedContentOptions,
): UseSanityContextBasedContentEager | UseSanityContextBasedContentLazy {
  const currentContext = useContext(SanitySiteContext)

  return useMemo(
    () =>
      options
        ? {
            currentContext,
            shouldDisplay: shouldDisplayContentFor(currentContext)(options),
          }
        : {
            currentContext,
            shouldDisplayContent: shouldDisplayContentFor(currentContext),
          },
    [currentContext, options],
  )
}
